import * as React from "react";
import { Check, Paperclip, PenLine, Send, Trash2 } from "lucide-react";
import moment from "../../../utilities/Moment";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { AnyAction, Dispatch } from "redux";
import { DashboardController, ExpensesController } from "../../../controllers";
import ERoute from "../../../ERoute";
import { showConfirmModal, showExpenseModal } from "../../../store/modals/actions";
import { DashboardExpenseSource, DashboardExpenseToProcess, DashboardExpensesToProcess } from "../../../types";
import Notification from "../../../utilities/Notification";
import NumberFormatter from "../../../utilities/NumberFormatter";
import CardEmptyInfo from "../../Card/CardEmptyInfo";
import PageLoader from "../../Page/PageLoader";
import Panel from "../../Panel/Panel";
import { Button, buttonVariants } from "../../ui/button";

interface IProps {
  currency: string;
  numberFormat: string;
}

const ExpensesToProcessWidget = (props: IProps) => {
  const { numberFormat } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch() as Dispatch<AnyAction>;
  const [isLoading, setIsLoading] = React.useState(true);
  const [data, setData] = React.useState<DashboardExpensesToProcess | null>(null);
  const [pendingIds, setPendingIds] = React.useState<string[]>([]);

  React.useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setData(await DashboardController.getExpensesToProcess());
    } catch (error) {
      console.error("Failed to fetch expenses to process:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Keeps the row's buttons disabled while its request is in flight
  const withPending = async (expense: DashboardExpenseToProcess, action: () => Promise<void>) => {
    setPendingIds((ids) => [...ids, expense.id]);

    try {
      await action();
    } catch (error) {
      console.error("Failed to process expense:", error);
    } finally {
      setPendingIds((ids) => ids.filter((id) => id !== expense.id));
    }
  };

  const onReviewClick = (expense: DashboardExpenseToProcess) =>
    withPending(expense, async () => {
      const response = await ExpensesController.update({ id: expense.id, reviewed: true });

      if (response.errors) {
        Notification.notifyError(t("ExpensesToProcessWidget::Oops something went wrong."));
      } else {
        Notification.notifySuccess(t("ExpensesToProcessWidget::Expense successfully reviewed."));
        await fetchData();
      }
    });

  const onExpenseClick = (expense: DashboardExpenseToProcess) => {
    dispatch(
      showExpenseModal({
        expense: { id: expense.id },
        onSubmit: () => fetchData(),
      })
    );
  };

  const onDeleteClick = (expense: DashboardExpenseToProcess) => {
    dispatch(
      showConfirmModal({
        title: t("ExpensesToProcessWidget::Delete expense"),
        description: t(
          "ExpensesToProcessWidget::You are about to delete an expense. This expense will be permanently deleted. Are you sure?"
        ),
        action: { label: t("ExpensesToProcessWidget::Delete"), isDestructive: true },
        onConfirm: () =>
          withPending(expense, async () => {
            await ExpensesController.delete(expense.id);
            Notification.notifySuccess(t("ExpensesToProcessWidget::Expense successfully removed."));
            await fetchData();
          }),
      })
    );
  };

  const renderSource = (source: DashboardExpenseSource) => {
    switch (source) {
      case "peppol":
        return (
          <span className="inline-flex items-center gap-1">
            <Send className="size-3" />
            {t("ExpensesToProcessWidget::Peppol")}
          </span>
        );
      case "upload":
        return (
          <span className="inline-flex items-center gap-1">
            <Paperclip className="size-3" />
            {t("ExpensesToProcessWidget::Upload")}
          </span>
        );
      case "manual":
        return (
          <span className="inline-flex items-center gap-1">
            <PenLine className="size-3" />
            {t("ExpensesToProcessWidget::Manual")}
          </span>
        );
    }
  };

  const title = t("ExpensesToProcessWidget::Expenses to process");
  const viewAllLink = (
    <Link to={ERoute.PATH_EXPENSES} className={buttonVariants({ variant: "link", size: "sm" })}>
      {t("ExpensesToProcessWidget::Go to expenses")}
    </Link>
  );

  if (isLoading || !data) {
    return (
      <Panel className="h-full flex flex-col mb-0!" contentClasses="min-h-0 flex-1 overflow-auto" title={title}>
        {isLoading ? <PageLoader /> : null}
      </Panel>
    );
  }

  return (
    <Panel
      className="h-full flex flex-col mb-0!"
      contentClasses="min-h-0 flex-1 flex flex-col p-0!"
      title={title}
      headerRight={viewAllLink}
    >
      {data.count === 0 ? (
        <div className="flex flex-1 items-center justify-center">
          <CardEmptyInfo icon="receipt" description={t("ExpensesToProcessWidget::All expenses are processed")} />
        </div>
      ) : (
        <>
          <div className="flex shrink-0 flex-wrap gap-x-8 gap-y-2 border-b px-4 py-3">
            <div>
              <div className="text-xs text-muted-foreground">{t("ExpensesToProcessWidget::To review")}</div>
              <div className="text-xl font-bold tabular-nums">{data.count}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">{t("ExpensesToProcessWidget::Total")}</div>
              <div className="text-xl font-bold tabular-nums">
                {NumberFormatter.formatCurrency(data.currency, numberFormat, Number(data.total))}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">{t("ExpensesToProcessWidget::Uploads")}</div>
              <div className="text-xl font-bold tabular-nums">{data.upload_count}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">{t("ExpensesToProcessWidget::Peppol")}</div>
              <div className="text-xl font-bold tabular-nums">{data.peppol_count}</div>
            </div>
          </div>

          {/* On phones widgets take their natural height, so cap the list there and let it scroll */}
          <ul className="m-0 min-h-0 flex-1 list-none overflow-auto p-0 in-[.dashboard-mobile]:max-h-80">
            {data.expenses.map((expense) => (
              <li key={expense.id} className="flex items-center gap-3 border-b px-4 py-2.5 last:border-b-0">
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => onExpenseClick(expense)}
                  onKeyDown={(e) => e.key === "Enter" && onExpenseClick(expense)}
                  className="min-w-0 flex-1 cursor-pointer"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="truncate font-medium">{expense.supplier_name || expense.name}</span>
                    <span className="shrink-0 translate-y-2 font-semibold tabular-nums">
                      {NumberFormatter.formatCurrency(expense.currency, numberFormat, Number(expense.amount))}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
                    {renderSource(expense.source)}
                    <span>·</span>
                    <span>
                      {t("ExpensesToProcessWidget::Received {{time}}", { time: moment(expense.received_at).fromNow() })}
                    </span>
                  </div>
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button
                    variant="outline"
                    size="icon-sm"
                    title={t("ExpensesToProcessWidget::Mark this expense as reviewed")}
                    aria-label={t("ExpensesToProcessWidget::Mark this expense as reviewed")}
                    disabled={pendingIds.includes(expense.id)}
                    onClick={() => onReviewClick(expense)}
                  >
                    <Check />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon-sm"
                    title={t("ExpensesToProcessWidget::Permanently delete this expense")}
                    aria-label={t("ExpensesToProcessWidget::Permanently delete this expense")}
                    disabled={pendingIds.includes(expense.id)}
                    onClick={() => onDeleteClick(expense)}
                  >
                    <Trash2 />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </Panel>
  );
};

export default ExpensesToProcessWidget;
